import { useEffect, useState } from "react";
import { useThemeStore } from "../store/theme";

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const DARK_QUERY = "(prefers-color-scheme: dark)";

const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
};

const applyTheme = (resolved: ResolvedTheme) => {
  if (typeof document === "undefined") return;
  // Our own attribute drives the token overrides in App.css.
  document.documentElement.setAttribute("data-theme", resolved);
  // Bootstrap 5.3+ ships a native dark theme keyed off this attribute,
  // which gives react-bootstrap modals, dropdowns, form controls and
  // other Bootstrap chrome dark variants for free.
  document.documentElement.setAttribute("data-bs-theme", resolved);
  // boneyard-js detects dark mode via a `.dark` ancestor (Tailwind
  // convention) — our app uses `data-theme`. Mirror it here so
  // boneyard's dark colours kick in alongside our own dark tokens.
  document.documentElement.classList.toggle("dark", resolved === "dark");
  // Favicon swap is intentionally NOT done here — it follows the
  // OS / browser color scheme via the `media` attribute on the
  // <link rel="icon"> tags in public/index.html, so it's independent
  // of the in-app theme toggle.
};

// Mounts once at app root. Reads `themePreference` from the persist store,
// resolves it to a concrete light/dark value (system → matches the OS),
// applies `data-theme` to <html>, and re-applies whenever the preference
// changes or — in system mode — when the OS preference changes.
export default function useTheme() {
  const themePreference = useThemeStore((s) => s.themePreference);

  useEffect(() => {
    const resolve = (): ResolvedTheme =>
      themePreference === "system" ? getSystemTheme() : themePreference;

    applyTheme(resolve());

    if (themePreference !== "system") return;
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mq = window.matchMedia(DARK_QUERY);
    const onChange = () => applyTheme(getSystemTheme());
    // Older Safari uses addListener; modern browsers use addEventListener.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [themePreference]);
}

// Read-only hook for components that need to react to the resolved theme
// (e.g. third-party widgets that take their own theme prop). Resolves the
// stored preference + system query and re-renders when either changes.
export function useResolvedTheme(): ResolvedTheme {
  const themePreference = useThemeStore((s) => s.themePreference);
  const resolve = (): ResolvedTheme =>
    themePreference === "system" ? getSystemTheme() : themePreference;
  const [resolved, setResolved] = useState<ResolvedTheme>(resolve);

  useEffect(() => {
    setResolved(resolve());
    if (themePreference !== "system") return;
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(DARK_QUERY);
    const onChange = () => setResolved(getSystemTheme());
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themePreference]);

  return resolved;
}
